import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { Map as GeographicMap, StyleSpecification } from "maplibre-gl";
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import "maplibre-gl/dist/maplibre-gl.css";
import { BASEMAP_STYLE_URL, dashboardBasemapStyle, geographicMapBounds, municipalityFitOptions } from "./municipality-map-basemap";
import type { MapFeature, Position, ViewportSize } from "./municipality-map-layout";
import { DEM_SOURCE_ID, HILLSHADE_LAYER_ID, isTerrainError, removeTerrainRelief, withTerrainRelief } from "./municipality-map-terrain";
import { municipalityTerrainMaskLayers } from "./municipality-terrain-mask";

export function useMunicipalityBasemap(features: readonly MapFeature[], viewport: ViewportSize) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<GeographicMap | null>(null);
  const [revision, setRevision] = useState(0);
  const [status, setStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  const [terrainStatus, setTerrainStatus] = useState<"loading" | "ready" | "unavailable">("loading");
  const bounds = useMemo(() => geographicMapBounds(features), [features]);
  const hasGeometry = Boolean(bounds);
  const featuresRef = useRef(features);
  const viewportRef = useRef(viewport);
  const initialFitDone = useRef(false);
  useEffect(() => { featuresRef.current = features; }, [features]);
  useEffect(() => { viewportRef.current = viewport; }, [viewport]);

  useEffect(() => {
    if (!hasGeometry || !containerRef.current) return;
    const container = containerRef.current;
    const controller = new AbortController();
    let instance: GeographicMap | undefined;
    let frame = 0;
    let failed = false;
    let terrainFailed = false;
    let terrainTimer: ReturnType<typeof setTimeout> | undefined;
    setStatus("loading");
    setTerrainStatus("loading");
    const disableTerrain = () => {
      if (controller.signal.aborted || terrainFailed) return;
      terrainFailed = true;
      clearTimeout(terrainTimer);
      setTerrainStatus("unavailable");
      // Finish the current source event before removing its source/cache.
      queueMicrotask(() => {
        if (!controller.signal.aborted && instance) removeTerrainRelief(instance);
      });
    };

    // Lazy-load WebGL only for a scoped map. Population updates never enter this effect.
    void import("maplibre-gl").then(async (library) => {
      if (controller.signal.aborted) return;
      library.setWorkerUrl(workerUrl);
      const isTouchFirstDevice = window.matchMedia("(pointer: coarse)").matches;
      instance = new library.Map({
        container,
        style: { version: 8, sources: {}, layers: [{ id: "blank", type: "background", paint: { "background-color": "#ffffff" } }] },
        center: [0, 0], zoom: 0, minZoom: 2, maxZoom: 18,
        pitch: 0, maxPitch: 0, bearing: 0,
        dragRotate: false, pitchWithRotate: false, keyboard: false,
        // On touch-first/mobile devices, a one-finger swipe belongs to the page,
        // not the map. Desktop users can still drag the map with a mouse.
        dragPan: !isTouchFirstDevice,
        // Wheel/trackpad scrolling and pinch gestures must not zoom the map.
        // Explicit zoom buttons and Reset still use the normal camera API.
        scrollZoom: false, touchZoomRotate: false,
        renderWorldCopies: false,
        // Permanent provider credits are rendered below the plot, never under labels.
        attributionControl: false,
      });
      instance.touchZoomRotate.disableRotation();
      instance.touchPitch.disable();
      if (isTouchFirstDevice) {
        // Keep vertical touch gestures available to the document scroller. MapLibre
        // remains clickable, while zoom/reset continue through the explicit controls.
        instance.dragPan.disable();
        instance.getCanvas().style.touchAction = "pan-y";
        instance.getCanvasContainer().style.touchAction = "pan-y";
        container.style.touchAction = "pan-y";
      }
      initialFitDone.current = false;
      // Fit the first successful load (including the blank fallback) only once
      // per instance. Style/DEM reloads and context restoration keep its camera.
      instance.once("load", () => {
        if (controller.signal.aborted || initialFitDone.current || !instance) return;
        const initialBounds = geographicMapBounds(featuresRef.current);
        if (!initialBounds) return;
        initialFitDone.current = true;
        instance.fitBounds(initialBounds, municipalityFitOptions(viewportRef.current));
      });
      instance.on("move", () => {
        if (!frame) frame = requestAnimationFrame(() => {
          frame = 0;
          if (!controller.signal.aborted) setRevision((value) => value + 1);
        });
      });
      instance.on("error", (event) => {
        if (isTerrainError(event)) {
          disableTerrain();
          return;
        }
        failed = true;
        if (!controller.signal.aborted) setStatus("unavailable");
      });
      instance.on("sourcedata", (event) => {
        if (event.sourceId === DEM_SOURCE_ID && event.isSourceLoaded && !terrainFailed && !controller.signal.aborted) {
          clearTimeout(terrainTimer);
          setTerrainStatus("ready");
        }
      });
      setMap(instance);
      // The blank geographic camera works even if the provider/style/tiles fail.
      const response = await fetch(BASEMAP_STYLE_URL, { signal: controller.signal });
      if (!response.ok) throw new Error("Basemap style unavailable");
      const style = withTerrainRelief(dashboardBasemapStyle(await response.json() as StyleSpecification));
      if (controller.signal.aborted) return;
      // Custom layers must be reinstalled if MapLibre restores a lost WebGL
      // context. The ID checks keep normal loads/remounts free of duplicates.
      instance.on("style.load", () => {
        if (controller.signal.aborted || terrainFailed || !instance || !instance.getLayer(HILLSHADE_LAYER_ID)) return;
        try {
          const nextLayer = style.layers[style.layers.findIndex((layer) => layer.id === HILLSHADE_LAYER_ID) + 1]?.id;
          const [capture, mask] = municipalityTerrainMaskLayers(instance, () => featuresRef.current);
          if (!instance.getLayer(capture.id)) instance.addLayer(capture, HILLSHADE_LAYER_ID);
          if (!instance.getLayer(mask.id)) instance.addLayer(mask, nextLayer);
        } catch { disableTerrain(); } // Fail closed: never show unmasked relief.
      });
      instance.setStyle(style);
      // A stalled optional provider must not keep the map loading indefinitely.
      terrainTimer = setTimeout(disableTerrain, 20_000);
      instance.once("idle", () => {
        if (!controller.signal.aborted && !failed) setStatus("ready");
      });
    }).catch(() => {
      if (!controller.signal.aborted) {
        setStatus("unavailable");
        setTerrainStatus("unavailable");
      }
    });

    return () => {
      controller.abort();
      clearTimeout(terrainTimer);
      cancelAnimationFrame(frame);
      setMap(null);
      instance?.remove();
    };
  }, [hasGeometry]);

  const fit = useCallback(() => {
    if (map && bounds) map.fitBounds(bounds, municipalityFitOptions(viewport));
  }, [map, bounds, viewport]);

  useEffect(() => {
    if (!map) return;
    // Resize the drawing surface without refitting after layout/data changes.
    map.resize();
  }, [map, viewport.width, viewport.height]);

  const project = useMemo(() => map ? (position: Position): Position => {
    const point = map.project(position);
    return [point.x, point.y];
  } : null, [map, revision]);

  return {
    containerRef, map, project, fit, status, terrainStatus,
    overlayContainer: map?.getCanvasContainer() ?? null,
    zoom: map?.getZoom() ?? null,
  };
}
